import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import {
  Dismiss24Regular,
  Link16Regular,
  ImageCopy20Regular,
  Copy16Regular,
} from "@fluentui/react-icons";
// @ts-expect-error qrcode should be imported partially not as an entire module, but typescript is trash and throws an error
import { QRCode } from "react-qr-code";
import { useRef, useState, useEffect } from "react";
import { useStyles } from "./QRCodeModal.styles";
import { IQRCodeModalProps } from "./QRCodeModal.types";
import { copyTextToClipboard } from "utils/generalUtils";

const RESETMESSAGETIME = 2000;

export const QRCodeModal = (props: IQRCodeModalProps): JSX.Element => {
  const { isOpen, onClose, url, title = "QR Code" } = props;
  const styles = useStyles();
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const [copyStatus, setCopyStatus] = useState<string>("");

  const handlePlainTextCopy = async () => {
    await copyTextToClipboard(url);
    setCopyStatus("Plain text link copied!");
    setTimeout(() => setCopyStatus(""), RESETMESSAGETIME);
  };

  const handleCopyQRCode = async (showMessage: boolean = true) => {
    try {
      const svg = qrCodeRef.current?.querySelector("svg");
      if (!svg) {
        // If SVG not ready yet, return false
        return false;
      }

      // Create canvas from SVG
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      canvas.width = 256;
      canvas.height = 256;

      const svgBlob = new Blob([svgData], {
        type: "image/svg+xml;charset=utf-8",
      });
      const svgUrl = URL.createObjectURL(svgBlob);

      return new Promise<boolean>((resolve) => {
        img.onload = async () => {
          if (ctx) {
            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, 256, 256);
            ctx.drawImage(img, 0, 0, 256, 256);

            canvas.toBlob(async (blob) => {
              if (blob) {
                try {
                  await navigator.clipboard.write([
                    new ClipboardItem({ "image/png": blob }),
                  ]);
                  if (showMessage) {
                    setCopyStatus("QR code copied as image!");
                    setTimeout(() => setCopyStatus(""), RESETMESSAGETIME);
                  }
                  resolve(true);
                } catch (error) {
                  console.error("Failed to copy QR code:", error);
                  if (showMessage) {
                    setCopyStatus("Failed to copy image");
                    setTimeout(() => setCopyStatus(""), RESETMESSAGETIME);
                  }
                  resolve(false);
                }
              }
              URL.revokeObjectURL(svgUrl);
            }, "image/png");
          }
        };
        img.onerror = () => {
          resolve(false);
        };
        img.src = svgUrl;
      });
    } catch (error) {
      console.error("Failed to copy QR code:", error);
      if (showMessage) {
        setCopyStatus("Failed to copy image");
        setTimeout(() => setCopyStatus(""), RESETMESSAGETIME);
      }
      return false;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.surface}>
        {!url ? (
          <Spinner />
        ) : (
          <DialogBody>
            <DialogTitle className={styles.header}>
              <Text className={styles.title}>{title}</Text>
              <Button
                appearance="outline"
                icon={<Dismiss24Regular />}
                onClick={onClose}
                aria-label="Close"
              />
            </DialogTitle>
            <DialogContent className={styles.content}>
              <div
                className={styles.qrCodeContainer}
                ref={qrCodeRef}
                onClick={() => handleCopyQRCode(true)}
              >
                <QRCode
                  value={url}
                  size={256}
                  level="H"
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
              </div>
              <div className={styles.infoText} onClick={handlePlainTextCopy}>
                <Link16Regular className={styles.linkIcon} />
                <span className={styles.urlSpan}>{url}</span>
              </div>
              {copyStatus && (
                <Text className={styles.copyStatusText}>{copyStatus}</Text>
              )}
              <div className={styles.footer}>
                <Tooltip content="Copy text link" relationship="label">
                  <Button
                    appearance="secondary"
                    onClick={handlePlainTextCopy}
                    className={styles.copyButton}
                  >
                    <Copy16Regular />
                    <span>Copy text</span>
                  </Button>
                </Tooltip>
                <Tooltip content="Copy QR code as image" relationship="label">
                  <Button
                    appearance="secondary"
                    onClick={() => handleCopyQRCode(true)}
                    className={styles.copyButton}
                  >
                    <ImageCopy20Regular />
                    <span>Copy QR</span>
                  </Button>
                </Tooltip>
              </div>
            </DialogContent>
          </DialogBody>
        )}
      </DialogSurface>
    </Dialog>
  );
};
